import { useEffect, useState } from 'react';
import type { DocNote, DocNoteTarget } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate } from '../format';

const MAX = 1000;

function time(iso: string): string {
  const d = new Date(iso);
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Sənəd qeydləri (KT-76): yaradanın qeydi (server xəttin əvvəlində verir) + sonradan əlavə olunan qeydlər xətti (kim, nə vaxt, nə yazıb) və yeni qeyd.
 * Qeydlər dəyişmir və silinmir. Görmək və yazmaq hüququnu server yoxlayır (403 — panel gizlənir).
 */
export function NotesPanel({ target }: { target: DocNoteTarget }) {
  const [notes, setNotes] = useState<DocNote[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = 'docId' in target ? target.docId : target.paymentId;

  useEffect(() => {
    let cancelled = false;
    api
      .notes(target)
      .then((n) => !cancelled && setNotes(n))
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) setHidden(true);
        else setError(err instanceof ApiError ? err.message : 'Qeydlər yüklənmədi');
      });
    return () => {
      cancelled = true;
    };
    // Hədəf obyekti hər renderdə yenidir — yalnız id dəyişəndə yenidən yüklənir
  }, [key]);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const n = await api.noteAdd(target, text.trim());
      setNotes((cur) => [...(cur ?? []), n]);
      setText('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qeyd yazılmadı');
    } finally {
      setBusy(false);
    }
  }

  if (hidden) return null;
  return (
    <section className="notes-panel" aria-label="Qeydlər">
      <h3>Qeydlər</h3>
      {notes?.map((n) => (
        <p key={n.id} className="note-item">
          <span className="muted small">
            {n.authorName} · {time(n.createdAt)}
            {n.source === 'approval' && ' · təsdiqdə'}
            {n.source === 'created' && ' · sənəd yaradılarkən'}
          </span>
          <span className="note-body">{n.body}</span>
        </p>
      ))}
      {notes && notes.length === 0 && <p className="muted small">Qeyd yoxdur.</p>}
      {error && (
        <p className="bad small" role="alert">
          {error}
        </p>
      )}
      <div className="note-add">
        <textarea aria-label="Yeni qeyd" rows={2} maxLength={MAX} value={text} placeholder="Qeyd əlavə et…" onChange={(e) => setText(e.target.value)} />
        <button type="button" className="btn-small" disabled={busy || !text.trim()} onClick={() => void add()}>
          Əlavə et
        </button>
      </div>
    </section>
  );
}

/** Yaradılarkən qeyd: bir toxunuşla açılır ki, kassada satışı yavaşlatmasın (KT-76). */
export function NoteField({ value, onChange, max = 500 }: { value: string; onChange: (v: string) => void; max?: number }) {
  const [open, setOpen] = useState(value !== '');
  if (!open) {
    return (
      <button type="button" className="link-btn note-toggle" onClick={() => setOpen(true)}>
        + Qeyd
      </button>
    );
  }
  return (
    <label className="note-field">
      Qeyd
      <textarea rows={2} maxLength={max} value={value} autoFocus onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
